import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Search, Plus, Copy, Pencil, ExternalLink, Eye, EyeOff, Trash2, Lock, MapPin, TrendingUp, Globe,
} from "lucide-react";

const NAVY = "#1C4358";
const ScopeBadge = ({ kind = "GLOBAL" }) => {
  const map = { GLOBAL: ["rgba(28,67,88,0.10)", NAVY], LOCAL: ["rgba(47,125,90,0.12)", "#2F7D5A"], HYBRID: ["rgba(200,140,20,0.14)", "#B7791F"] };
  const [bg, c] = map[kind];
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: bg, color: c }}>{kind}</span>;
};

export default function ReverseSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [pages, setPages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newPage, setNewPage] = useState({ admin_title: "", slug: "", matched_service_lane: "" });
  const [locOpen, setLocOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({ city: "", state_province: "", franchise_id: "", enabled: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, ln, fr] = await Promise.all([
        api.get("/site/reverse-search"),
        api.get("/site/service-lanes"),
        api.get("/site/my-franchises"),
      ]);
      setPages(rs.data.pages || []);
      setLocations(rs.data.locations || []);
      setLanes((ln.data.items || []).map((l) => ({ slug: l.slug, name: l.name })));
      setFranchises(fr.data.items || []);
    } catch { toast.error("Could not load reverse search pages."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    try { await api.put(`/site/reverse-search/${p.id}`, { active: !p.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const clonePage = async (p) => {
    try { const { data } = await api.post(`/site/reverse-search/${p.id}/clone`); toast.success("Cloned as draft."); navigate(`/site/reverse-search/${data.page.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Clone failed."); }
  };
  const removePage = async (p) => {
    if (!window.confirm(`Delete "${p.admin_title}"?`)) return;
    try { await api.delete(`/site/reverse-search/${p.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };
  const createPage = async () => {
    if (!newPage.admin_title.trim()) { toast.error("Title is required."); return; }
    try {
      const { data } = await api.post("/site/reverse-search", { ...newPage, matched_service_lane: newPage.matched_service_lane || null });
      toast.success("Page created (draft)."); setCreateOpen(false); setNewPage({ admin_title: "", slug: "", matched_service_lane: "" });
      navigate(`/site/reverse-search/${data.page.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };
  const createLoc = async () => {
    if (!newLoc.city.trim()) { toast.error("City is required."); return; }
    try {
      await api.post("/site/reverse-search/locations", { ...newLoc, franchise_id: newLoc.franchise_id || null });
      toast.success("Location added."); setLocOpen(false); setNewLoc({ city: "", state_province: "", franchise_id: "", enabled: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed."); }
  };
  const toggleLoc = async (l) => {
    try { await api.put(`/site/reverse-search/locations/${l.id}`, { enabled: !l.enabled }); load(); }
    catch { toast.error("Update failed."); }
  };
  const removeLoc = async (l) => {
    if (!window.confirm(`Remove ${l.city}?`)) return;
    try { await api.delete(`/site/reverse-search/locations/${l.id}`); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="reverse-search-page-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Search size={22} style={{ color: NAVY }} />
            <h1 className="text-2xl font-display font-bold text-[#1C4358]">Reverse Search / Local SEO</h1>
            <ScopeBadge kind="GLOBAL" />
          </div>
          <p className="text-[14px] text-[#4B5563] mt-1">Situation × location landing pages that start with the family's question. Public at <span className="font-mono">/find/&lt;topic&gt;</span>.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] px-3 h-10 rounded-lg hover:bg-[#1C4358]/6"><Globe size={15} /> sitemap.xml</a>
          {isSuper && <Button data-testid="create-reverse-btn" onClick={() => setCreateOpen(true)} className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white"><Plus size={16} /> New page</Button>}
        </div>
      </div>

      {!isSuper && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-[rgba(28,67,88,0.05)] px-4 py-3 text-[14px] text-[#1C4358]"><Lock size={15} /> Global content is read-only for your role. Edit local details under Local Overrides.</div>
      )}

      <div className="mt-6 space-y-3">
        {pages.map((p) => (
          <Card key={p.id} data-testid={`reverse-row-${p.slug}`} className="p-4 border-[#E6EDF2] flex items-center gap-4">
            <img src={p.hero_image_url} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold text-[#1C4358] text-[16px] truncate">{p.admin_title}</span>
                {!p.active && <Badge variant="outline" className="text-[10px] border-[#9C1E22]/40 text-[#9C1E22]">Inactive</Badge>}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: p.approval_status === "approved" ? "rgba(47,125,90,0.12)" : "rgba(200,140,20,0.14)", color: p.approval_status === "approved" ? "#2F7D5A" : "#B7791F" }}>{p.approval_status}</span>
                {p.noindex && <Badge variant="outline" className="text-[10px] border-[#B7791F]/40 text-[#B7791F]">noindex</Badge>}
                {!p.include_in_sitemap && <Badge variant="outline" className="text-[10px] border-[#4B5563]/30 text-[#4B5563]">no sitemap</Badge>}
              </div>
              <div className="text-[13px] text-[#4B5563] mt-0.5">/find/{p.slug} · <span className="inline-flex items-center gap-1 text-[#2F7D5A]"><TrendingUp size={12} /> {p.lead_count} leads</span></div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <a href={`/find/${p.slug}`} target="_blank" rel="noreferrer" title="Preview" data-testid={`reverse-preview-${p.slug}`} className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8"><ExternalLink size={16} /></a>
              {isSuper && <>
                <button onClick={() => clonePage(p)} title="Clone" data-testid={`reverse-clone-${p.slug}`} className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8"><Copy size={16} /></button>
                <button onClick={() => toggleActive(p)} title={p.active ? "Deactivate" : "Activate"} data-testid={`reverse-toggle-${p.slug}`} className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8">{p.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => removePage(p)} title="Delete" data-testid={`reverse-delete-${p.slug}`} className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#9C1E22] hover:bg-[#9C1E22]/8"><Trash2 size={16} /></button>
              </>}
              <Button variant="outline" size="sm" data-testid={`reverse-edit-${p.slug}`} onClick={() => navigate(`/site/reverse-search/${p.id}`)} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Pencil size={14} /> {isSuper ? "Edit" : "View"}</Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Target locations */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><MapPin size={18} style={{ color: NAVY }} /><h2 className="text-lg font-display font-bold text-[#1C4358]">Target locations</h2><ScopeBadge kind="LOCAL" /></div>
          {isSuper && <Button variant="outline" size="sm" data-testid="add-location-btn" onClick={() => setLocOpen(true)} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Plus size={14} /> Add city</Button>}
        </div>
        <p className="text-[13px] text-[#4B5563] mt-1">Only enabled locations with local info generate live <span className="font-mono">/find/&lt;topic&gt;/&lt;city&gt;</span> pages.</p>
        <div className="mt-4 space-y-2">
          {locations.length === 0 ? <p className="text-sm text-muted-foreground">No target locations yet.</p> : locations.map((l) => (
            <Card key={l.id} data-testid={`location-row-${l.slug}`} className="p-3 border-[#E6EDF2] flex items-center justify-between gap-3">
              <div>
                <span className="font-semibold text-[#1C4358]">{l.city}{l.state_province ? `, ${l.state_province}` : ""}</span>
                <span className="text-[12px] text-[#4B5563] ml-2 font-mono">/{l.slug}</span>
                {l.franchise_id && <Badge className="ml-2 text-[10px] bg-[#9BB9CE]/25 text-[#1C4358] border-0">franchise-linked</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-[12px] text-[#252221]"><Switch checked={!!l.enabled} disabled={!isSuper} onCheckedChange={() => toggleLoc(l)} data-testid={`location-toggle-${l.slug}`} /> {l.enabled ? "Live" : "Off"}</label>
                {isSuper && <button onClick={() => removeLoc(l)} className="text-[#9C1E22]"><Trash2 size={15} /></button>}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Create page dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-reverse-dialog">
          <DialogHeader><DialogTitle>New reverse search page</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="new-reverse-title" value={newPage.admin_title} onChange={(e) => setNewPage((n) => ({ ...n, admin_title: e.target.value }))} placeholder="e.g. Help Selling Dad's Farm" /></div>
            <div className="space-y-1.5"><Label>Slug (optional)</Label><Input value={newPage.slug} onChange={(e) => setNewPage((n) => ({ ...n, slug: e.target.value }))} placeholder="auto from title" /></div>
            <div className="space-y-1.5"><Label>Matched service lane</Label>
              <Select value={newPage.matched_service_lane || "none"} onValueChange={(v) => setNewPage((n) => ({ ...n, matched_service_lane: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="new-reverse-lane"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{lanes.map((l) => <SelectItem key={l.slug} value={l.slug}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button data-testid="new-reverse-submit" onClick={createPage} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Create draft</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add location dialog */}
      <Dialog open={locOpen} onOpenChange={setLocOpen}>
        <DialogContent data-testid="add-location-dialog">
          <DialogHeader><DialogTitle>Add target city</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>City</Label><Input data-testid="new-loc-city" value={newLoc.city} onChange={(e) => setNewLoc((n) => ({ ...n, city: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>State/Province</Label><Input data-testid="new-loc-state" value={newLoc.state_province} onChange={(e) => setNewLoc((n) => ({ ...n, state_province: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Link to franchise (for local info)</Label>
              <Select value={newLoc.franchise_id || "none"} onValueChange={(v) => setNewLoc((n) => ({ ...n, franchise_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="new-loc-franchise"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={newLoc.enabled} onCheckedChange={(v) => setNewLoc((n) => ({ ...n, enabled: v }))} /> Enable immediately</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLocOpen(false)}>Cancel</Button><Button data-testid="new-loc-submit" onClick={createLoc} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Add location</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
